import { useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { BlockItemList, RichTextField } from "./blockEditorParts";
import { ButtonFields } from "./ButtonFields";
import { emptyButton, withoutEmptySection } from "./landingEditorParts";
import { SectionFields, type SectionFormValues, toSectionFormValues } from "./SectionFields";
import type { BlockButton, BlockContentErrors, CallToActionContent } from "./types";
import { useContentChange } from "./useContentChange";
import styles from "../CMS.module.css";

type CallToActionEditorProps = {
  value: CallToActionContent;
  onChange: (value: CallToActionContent) => void;
  errors: BlockContentErrors;
};

type CallToActionFormValues = SectionFormValues & {
  text: string;
  buttons: BlockButton[];
};

function toContent(
  initial: CallToActionContent,
  values: CallToActionFormValues,
): CallToActionContent {
  return withoutEmptySection(
    {
      ...initial,
      ...values,
      buttons: values.buttons.map(button => ({
        ...button,
        label: button.label.trim(),
        href: button.href.trim(),
      })),
    },
    ["text"],
  );
}

export function CallToActionEditor({ value, onChange, errors }: CallToActionEditorProps) {
  const [initial] = useState(value);
  const { control, register, watch } = useForm<CallToActionFormValues>({
    defaultValues: {
      ...toSectionFormValues(value),
      text: value.text ?? "",
      buttons: value.buttons,
    },
  });
  const { fields, append, remove, move } = useFieldArray<
    CallToActionFormValues,
    "buttons",
    "fieldKey"
  >({ control, name: "buttons", keyName: "fieldKey" });
  useContentChange(watch, onChange, values => toContent(initial, values));

  return (
    <div className={styles.blockEditor}>
      <SectionFields register={register} idPrefix="call-to-action" errors={errors} />

      <RichTextField
        control={control}
        name="text"
        id="call-to-action-text"
        label="Text (optional)"
        emptyAsBlank
      />

      <BlockItemList
        noun="Button"
        keys={fields.map(field => field.fieldKey)}
        onMove={(from, to) => move(from, to)}
        onRemove={remove}
        onAdd={() => append(emptyButton("primary"))}
        renderItem={(index, key) => (
          <ButtonFields
            register={register}
            name={`buttons.${index}`}
            idPrefix={`call-to-action-button-${key}`}
            legend={`Button ${index + 1}`}
            errors={errors}
          />
        )}
      />
      {errors.buttons && (
        <p className={styles.fieldError} role="alert">
          {errors.buttons}
        </p>
      )}
    </div>
  );
}
